<template>
  <section class="team-section">
    <div class="container">
      <div class="section-header animate">
        <h2 class="section-title">
          {{ $t('team.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('team.subtitle') }}
        </p>
      </div>

      <!-- Leadership Row -->
      <div class="leadership-row animate animate--delay-1">
        <div class="team-member leadership">
          <div class="member-card">
            <div class="image-container">
              <img
                :src="`/images/team/${ceoMember.image}-1.jpg`"
                :alt="ceoMember.name"
                class="member-photo default"
              >
              <img
                :src="`/images/team/${ceoMember.image}-2.jpg`"
                :alt="ceoMember.name"
                class="member-photo hover"
              >
              <div class="leadership-glow" />
            </div>
            <div class="member-info">
              <h3 class="member-name">
                {{ ceoMember.name }}
              </h3>
              <p class="member-position">
                Chief Executive Officer
              </p>
            </div>
          </div>
        </div>

        <div class="team-member leadership">
          <div class="member-card">
            <div class="image-container">
              <img
                :src="`/images/team/${cooMember.image}-1.jpg`"
                :alt="cooMember.name"
                class="member-photo default"
              >
              <img
                :src="`/images/team/${cooMember.image}-2.jpg`"
                :alt="cooMember.name"
                class="member-photo hover"
              >
              <div class="leadership-glow" />
            </div>
            <div class="member-info">
              <h3 class="member-name">
                {{ cooMember.name }}
              </h3>
              <p class="member-position">
                Chief Operating Officer
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Team Grid -->
      <div class="team-grid animate animate--delay-2">
        <div
          v-for="(member, index) in teamMembers"
          :key="member.image"
          class="team-member"
          :class="{ 'animate--delay-3': index % 2 === 1 }"
        >
          <div class="member-card">
            <div class="image-container">
              <img
                :src="`/images/team/${member.image}-1.jpg`"
                :alt="member.name"
                class="member-photo default"
              >
              <img
                :src="`/images/team/${member.image}-2.jpg`"
                :alt="member.name"
                class="member-photo hover"
              >
            </div>
            <div class="member-info">
              <h3 class="member-name">
                {{ member.name }}
              </h3>
              <p class="member-position">
                {{ member.position }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';

// Define team data - adjust image names based on actual files
const ceoMember = ref({
  image: 'gil', // CEO
  name: 'Gilberto Avalos',
});

const cooMember = ref({
  image: 'julio', // COO
  name: 'Julio Pulido',
});

const teamMembers = ref([
  {
    image: 'ricardo',
    name: 'Ricardo Avalos',
    position: 'Social Media',
  },
  {
    image: 'danny',
    name: 'Daniel Nuño',
    position: 'Public Relations',
  },
  {
    image: 'rafa',
    name: 'Rafael Delgado',
    position: 'Head of Distribution',
  },
  {
    image: 'josue',
    name: 'Josué Pérez',
    position: 'Distribution & QA',
  },
  {
    image: 'luis',
    name: 'Luis Arellano',
    position: 'Digital Content Creator',
  },
  {
    image: 'angel',
    name: 'Angel Gaitán',
    position: 'Customer Happiness',
  },
  {
    image: 'ricar2',
    name: 'Ricardo Fletes',
    position: 'Distribution & QA',
  },
  {
    image: 'sebas',
    name: 'Sebastian Ontiveros',
    position: 'Content Creator',
  },
]);


</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.team-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;

  // Dark mode support
  .body--dark & {
    background: linear-gradient(135deg, #0f1419 0%, #1a202c 100%);
  }

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 107, 107, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(78, 205, 196, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;

    @media (max-width: $mobile) {
      padding: 0 1rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 5rem;
    display: block;
    width: 100%;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 3rem;
      font-weight: 800;
      color: #2d3748;
      margin-bottom: 1rem;
      display: block;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;

      .body--dark & {
        color: #f7fafc;
        background: linear-gradient(135deg, #90cdf4 0%, #a78bfa 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      @media (max-width: $tablet) {
        font-size: 2.5rem;
      }

      @media (max-width: $mobile) {
        font-size: 2rem;
      }
    }

    .section-subtitle {
      font-size: 1.25rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;
      line-height: 1.6;
      display: block;

      .body--dark & {
        color: #a0aec0;
      }

      @media (max-width: $mobile) {
        font-size: 1.1rem;
      }
    }
  }

  .leadership-row {
    display: flex;
    justify-content: center;
    gap: 4rem;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      gap: 3rem;
      margin-bottom: 5rem;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      gap: 2rem;
      margin-bottom: 4rem;
      align-items: center;
    }

    .team-member.leadership {
      .member-card {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: 3px solid #ffd700;
        transform: scale(1.1);

        .body--dark & {
          background: linear-gradient(135deg, #4c1d95 0%, #5b21b6 100%);
          border: 3px solid #f59e0b;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6);
        }

        @media (max-width: $mobile) {
          transform: scale(1);
        }

        .image-container {
          border: 4px solid rgba(255, 215, 0, 0.5);
        }

        .member-info {
          color: white;

          .member-name {
            color: #ffd700;
            font-size: 1.4rem;
          }

          .member-position {
            color: rgba(255, 255, 255, 0.9);
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 600;
          }
        }
      }
    }
  }

  .team-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 3rem;
    justify-items: center;

    @media (max-width: $tablet) {
      gap: 2rem;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }

    @media (max-width: $mobile) {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
    }
  }

  .team-member {
    width: 100%;
    max-width: 300px;

    .member-card {
      background: white;
      border-radius: 20px;
      padding: 2rem 1.5rem;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      text-align: center;
      position: relative;
      overflow: hidden;

      .body--dark & {
        background: #1a202c;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      }

      @media (max-width: $mobile) {
        padding: 1.5rem 1rem;
        border-radius: 15px;
      }

      // Hover effects only on devices that support hover
      @media (hover: hover) {
        &:hover {
          transform: translateY(-10px) scale(1.02);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);

                  .body--dark & {
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
        }
        }
      }

      // Touch-friendly effects for mobile
      @media (hover: none) {
        &:active {
          transform: translateY(-5px) scale(1.01);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);

          .body--dark & {
            box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
          }

          .image-container {
            border-color: #667eea;

            .member-photo.default {
              opacity: 0;
              transform: scale(1.05);
              filter: grayscale(100%) contrast(1.1);
            }

            .member-photo.hover {
              opacity: 1;
              transform: scale(1);
              filter: grayscale(0%) saturate(1.2);
            }

            .leadership-glow {
              opacity: 0.7;
            }

            .cool-overlay {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }

          .member-info {
            .member-name {
              color: #667eea;
              transform: scale(1.05);
            }
          }
        }
      }

      @media (hover: hover) {
        &:hover {
          .image-container {
            border-color: #667eea;

            .member-photo.default {
              opacity: 0;
              transform: scale(1.05);
              filter: grayscale(100%) contrast(1.1);
            }

            .member-photo.hover {
              opacity: 1;
              transform: scale(1);
              filter: grayscale(0%) contrast(1) saturate(1.2) brightness(1.05);

              @media (max-width: $mobile) {
                filter: grayscale(0%) saturate(1.2); // Simpler filter on mobile
              }
            }

            .leadership-glow {
              opacity: 0.7;
            }

            .cool-overlay {
              opacity: 1;
              transform: translate(-50%, -50%);
            }
          }

          .member-info {
            .member-name {
              color: #667eea;
              transform: scale(1.05);
            }
          }
        }
      }
    }

    .image-container {
      position: relative;
      width: 150px;
      height: 150px;
      margin: 0 auto 2rem;
      border-radius: 50%;
      overflow: hidden;
      border: 4px solid #e2e8f0;
      transition: all 0.3s ease;

      .body--dark & {
        border-color: #2d3748;
      }

      @media (max-width: $mobile) {
        width: 120px;
        height: 120px;
        margin-bottom: 1.5rem;
      }

      .member-photo {
        position: absolute;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: all 0.3s ease;

        &.default {
          opacity: 1;
          transform: scale(1);
          z-index: 1;
          filter: grayscale(100%) contrast(1.1);

          @media (max-width: $mobile) {
            filter: grayscale(100%); // Simpler filter on mobile for performance
          }
        }

        &.hover {
          opacity: 0;
          transform: scale(0.95);
          z-index: 2;
          filter: grayscale(0%) contrast(1) saturate(1.1);

          @media (max-width: $mobile) {
            filter: grayscale(0%) saturate(1.1); // Simpler filter on mobile
          }
        }
      }

            .leadership-glow {
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        background: linear-gradient(45deg, #ffd700, #ffed4e, #ffd700);
        border-radius: 50%;
        opacity: 0;
        z-index: 0;
        transition: opacity 0.3s ease;
      }

      .cool-overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 5;

        .sunglasses {
          font-size: 2rem;
          text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);

          @media (max-width: $mobile) {
            font-size: 1.5rem;
          }
        }
      }
    }

    .member-info {
      .member-name {
        font-size: 1.3rem;
        font-weight: 700;
        color: #2d3748;
        margin-bottom: 0.5rem;
        transition: all 0.3s ease;

        .body--dark & {
          color: #f7fafc;
        }

        @media (max-width: $mobile) {
          font-size: 1.1rem;
        }
      }

      .member-position {
        font-size: 1rem;
        color: #718096;
        font-weight: 500;

        .body--dark & {
          color: #a0aec0;
        }

        @media (max-width: $mobile) {
          font-size: 0.9rem;
        }
      }
    }
  }
}


</style>
